import type { DeviceController } from "./device-interface"
import { hashActions as hashActionsContent } from "../funscript-hash"

export interface HandyStatus {
  connected: boolean
  state: number
  mode: number
}

export interface HandyInfo {
  fwVersion: string
  fwStatus: number
  hwVersion: number
  model: string
  branch: string
}

export type HandyApiVersion = "v2" | "v3"

export class HandyDevice implements DeviceController {
  type = "handy" as const
  // v2 (stable) targets firmware 3+, is the long-supported default. v3
  // (beta) is required for firmware 4+ device features going forward but is
  // itself still beta per Handy's own spec — kept switchable per-device so a
  // user on v3 can fall back to v2 if something regresses.
  // See lib/devices/handy-device-v3-notes.md for the full endpoint diff this
  // is built from and which parts are unverified against a live device.
  private apiVersion: HandyApiVersion = "v2"
  private baseUrl = "/api/handy/v2"
  private connectionKey: string | null = null
  private lastPingTime = 0
  private isConnected = false
  private pingInterval: NodeJS.Timeout | null = null
  private connectionMonitorActive = false
  private keepAwakeInterval: NodeJS.Timeout | null = null
  private keepAwakeInFlight = false
  private lastKeepAwakeTime = 0
  private lastSetMode: number | null = null

  private lastCommandSliderVal = 50
  private lastSendTime = 0
  private pendingSliderVal: number | null = null
  private pendingDuration: number | undefined = undefined
  private sendTimerId: NodeJS.Timeout | null = null
  
  // Add smoothing and prediction for better real-time control
  private positionHistory: Array<{ position: number; timestamp: number }> = []
  private readonly HISTORY_SIZE = 5
  private lastVelocity = 0
  
  // HAMP mode state
  private isHAMPMode = false
  private hampVelocity = 0
  private hampUpdateInterval: NodeJS.Timeout | null = null
  
  // Phase 3: Velocity prediction state
  private velocityHistory: number[] = []
  private readonly VELOCITY_HISTORY_SIZE = 3
  private predictedVelocity = 0

  // Real-time drawing optimized parameters (for live iteration)
  private readonly REALTIME_MIN_INTERVAL_MS = 20      // 50 commands/second for responsiveness
  private readonly REALTIME_DURATION_MS = 80          // Faster response time
  private readonly REALTIME_DELTA_THRESHOLD = 1       // More sensitive to movements

  // Playback parameters (for scripted content)
  private readonly FULL_STROKE_DURATION_MS = 150      // Smooth playback
  private readonly MIN_DURATION_MS = 50               // Minimum duration
  private readonly MAX_DURATION_MS = 400              // Maximum duration
  private readonly DELTA_THRESHOLD_PERCENT = 2        // Filter small movements
  private readonly MIN_INTERVAL_MS = 40               // 25 commands/second

  private readonly KEEP_AWAKE_INTERVAL_MS = 30_000
  private readonly KEEP_AWAKE_IDLE_AFTER_MS = 20_000

  private deviceSettings: {
    minPosition: number
    maxPosition: number
  } = {
    minPosition: 0,
    maxPosition: 100,
  }

  setConnectionKey(key: string) {
    if (key !== this.connectionKey) {
      this.lastSetMode = null
      this.lastSetupUrl = null
      this.serverTimeSyncedAt = 0
      this.hsspActive = false
    }
    this.connectionKey = key
  }

  getConnectionKey(): string | null {
    return this.connectionKey
  }

  setApiVersion(version: HandyApiVersion) {
    if (version !== this.apiVersion) {
      this.lastSetMode = null
      this.lastSetupUrl = null
      this.serverTimeSyncedAt = 0
      this.hsspActive = false
    }
    this.apiVersion = version
    this.baseUrl = version === "v3" ? "/api/handy/v3" : "/api/handy/v2"
  }

  getApiVersion(): HandyApiVersion {
    return this.apiVersion
  }

  // HSSP is mode 1 and HDSP is mode 2. Using the wrong number silently puts
  // the device in the other protocol instead of erroring, so every /mode call
  // in this file goes through these instead of a hardcoded 1/2.
  private get MODE_HDSP(): number {
    return 2
  }
  private get MODE_HSSP(): number {
    return 1
  }

  private getHeaders(): HeadersInit {
    if (!this.connectionKey) {
      throw new Error("No connection key set")
    }
    // The X-Api-Key v3 requires is injected server-side by the /api/handy/v3
    // proxy route — never sent from the client, same connection-key-only
    // header set works for both versions here.
    return {
      "X-Connection-Key": this.connectionKey,
      "Content-Type": "application/json",
    }
  }

  // v3 wraps every response as {result: ...} on success or {error: ...} on
  // failure; v2 returns the payload flat. servertime is the one exception —
  // it's unauthenticated/unwrapped in v3 too, so callers of that endpoint
  // read it directly rather than through this helper.
  private unwrap<T = any>(raw: any): T {
    if (this.apiVersion === "v3" && raw && typeof raw === "object" && "result" in raw) {
      return raw.result
    }
    return raw
  }

  private computeDurationForDelta(delta: number, isRealTime: boolean = false): number {
    if (delta <= 0.1) return 0
    
    // Use different duration calculations for real-time vs playback
    const baseDuration = isRealTime 
      ? this.REALTIME_DURATION_MS * Math.sqrt(delta / 100)  // Faster for real-time
      : this.FULL_STROKE_DURATION_MS * Math.sqrt(delta / 100)  // Smoother for playback
    
    // Add velocity-based adjustment
    const velocityFactor = isRealTime
      ? Math.max(0.5, 1 - (delta / 150))  // More aggressive for real-time
      : Math.max(0.6, 1 - (delta / 200))  // Conservative for playback
    
    const adjustedDuration = baseDuration * velocityFactor
    
    const minDur = isRealTime ? this.REALTIME_DURATION_MS : this.MIN_DURATION_MS
    const maxDur = this.MAX_DURATION_MS
    
    return Math.max(minDur, Math.min(Math.round(adjustedDuration), maxDur))
  }

  async checkConnection(): Promise<boolean> {
    if (!this.connectionKey) {
      console.log("[v0] [Handy] No connection key set")
      return false
    }

    // Return cached result if checked within the last 10 seconds
    if (this.lastPingTime > 0 && Date.now() - this.lastPingTime < 10_000) {
      return this.isConnected
    }

    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000)

      const response = await fetch(`${this.baseUrl}/connected`, {
        headers: this.getHeaders(),
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        this.isConnected = false
        this.lastPingTime = Date.now()
        return false
      }

      const data = this.unwrap<{ connected?: boolean }>(await response.json())
      this.isConnected = data?.connected === true
      this.lastPingTime = Date.now()
      if (this.isConnected && this.connectionMonitorActive) {
        this.startKeepAwake()
      } else {
        this.stopKeepAwake()
      }

      return this.isConnected
    } catch (error) {
      console.log("[v0] [Handy] Connection check error:", error)
      this.isConnected = false
      this.lastPingTime = Date.now()
      this.stopKeepAwake()
      return false
    }
  }

  private startKeepAwake() {
    if (this.keepAwakeInterval) return

    this.sendKeepAwake().catch((error) => {
      console.warn("[Handy] Initial keep-awake failed:", error)
    })

    this.keepAwakeInterval = setInterval(() => {
      this.sendKeepAwake().catch((error) => {
        console.warn("[Handy] Keep-awake failed:", error)
      })
    }, this.KEEP_AWAKE_INTERVAL_MS)
  }

  private stopKeepAwake() {
    if (this.keepAwakeInterval) {
      clearInterval(this.keepAwakeInterval)
      this.keepAwakeInterval = null
    }
    this.keepAwakeInFlight = false
  }

  private shouldSkipKeepAwake(now: number, performanceNow: number): boolean {
    if (!this.connectionKey || !this.isConnected) return true
    if (this.keepAwakeInFlight) return true
    if (this.isHAMPMode || this.hsspActive) return true
    if (this.lastSendTime > 0 && performanceNow - this.lastSendTime < this.KEEP_AWAKE_IDLE_AFTER_MS) return true
    if (now - this.lastKeepAwakeTime < this.KEEP_AWAKE_INTERVAL_MS - 1_000) return true
    return false
  }

  private async sendKeepAwake(): Promise<boolean> {
    const now = Date.now()
    const performanceNow = performance.now()
    if (this.shouldSkipKeepAwake(now, performanceNow)) return true

    this.keepAwakeInFlight = true
    try {
      // Use a device RPC, not a mode write: another tab/adapter may own playback.
      const response = await fetch(`${this.baseUrl}/hstp/time`, {
        headers: this.getHeaders(),
      })

      if (!response.ok) {
        const errorText = await response.text().catch(() => "")
        console.warn("[Handy] Keep-awake clock request failed:", response.status, errorText)
        return false
      }

      this.lastKeepAwakeTime = now
      return true
    } catch (error) {
      console.warn("[Handy] Keep-awake request failed:", error)
      return false
    } finally {
      this.keepAwakeInFlight = false
    }
  }

  async getInfo(): Promise<HandyInfo | null> {
    if (!this.connectionKey) return null

    try {
      const response = await fetch(`${this.baseUrl}/info`, {
        headers: this.getHeaders(),
      })
      const raw = this.unwrap<any>(await response.json())
      if (!raw) return null
      if (this.apiVersion === "v3") {
        // v3 renames every field to snake_case and drops "branch" as a
        // concept — fw_feature_flags is the closest analogue, not a 1:1
        // match, but keeps the field populated for any UI reading it.
        return {
          fwVersion: raw.fw_version,
          fwStatus: raw.fw_status,
          hwVersion: raw.hw_model_no,
          model: raw.hw_model_name,
          branch: raw.fw_feature_flags,
        }
      }
      return raw
    } catch (error) {
      console.error("[Handy] Get info failed:", error)
      return null
    }
  }

  async getStatus(): Promise<HandyStatus | null> {
    if (!this.connectionKey) return null

    try {
      if (this.apiVersion === "v3") {
        // v3 dropped the single /status endpoint in favor of separate
        // per-protocol state endpoints — this composes a best-effort
        // equivalent from /mode + /connected. `state` has no direct v3
        // analogue (it was HAMP/HDSP-specific in v2), left 0.
        const [modeRes, connRes] = await Promise.all([
          fetch(`${this.baseUrl}/mode`, { headers: this.getHeaders() }),
          fetch(`${this.baseUrl}/connected`, { headers: this.getHeaders() }),
        ])
        const modeData = this.unwrap<any>(await modeRes.json())
        const connData = this.unwrap<any>(await connRes.json())
        return {
          connected: connData?.connected === true,
          state: 0,
          mode: modeData?.mode ?? 0,
        }
      }
      const response = await fetch(`${this.baseUrl}/status`, {
        headers: this.getHeaders(),
      })
      return await response.json()
    } catch (error) {
      console.error("[Handy] Get status failed:", error)
      return null
    }
  }

  private async setMode(modeNum: number): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/mode`, {
        method: "PUT",
        headers: this.getHeaders(),
        body: JSON.stringify({ mode: modeNum }),
      })
      return response.ok
    } catch (error) {
      console.error("[Handy] Set mode failed:", error)
      return false
    }
  }

  private async initializeHDSP(): Promise<boolean> {
    try {
      await fetch(`${this.baseUrl}/hamp/stop`, {
        method: "PUT",
        headers: this.getHeaders(),
      })

      await new Promise((r) => setTimeout(r, 200))

      // HDSP = MODE_HDSP (per-command positioning). HSSP = MODE_HSSP — position commands ignored there.
      const modeSet = await this.setMode(this.MODE_HDSP)
      if (!modeSet) {
        console.error("[Handy] Failed to set HDSP mode")
        return false
      }

      this.lastSetMode = this.MODE_HDSP

      // Reset the device's physical slide zone to full range (0–100%).
      // The firmware persists this across sessions — a prior app may have set it to a
      // narrow range, which would silently limit stroke amplitude even when we send
      // position=0/100 on v2. Our software min/max scaling is the only intended limiter.
      // v3 made /settings/slider read-only ("use the Handy onboarding app to change
      // this") — no PUT exists, so this reset is skipped there; a narrow zone set by
      // a prior app can only be cleared from Handy's own app on v3.
      if (this.apiVersion !== "v3") {
        await fetch(`${this.baseUrl}/slide`, {
          method: "PUT",
          headers: this.getHeaders(),
          body: JSON.stringify({ min: 0, max: 100 }),
        }).catch(() => { /* best-effort — don't block on failure */ })
      }

      await new Promise((r) => setTimeout(r, 200))

      return true
    } catch (error) {
      console.error("[Handy] HDSP initialization failed:", error)
      return false
    }
  }

  private async sendXPTForSliderVal(v: number, delta: number, isRealTime: boolean = false): Promise<boolean> {
    if (!this.isConnected || !this.connectionKey) {
      return false
    }

    if (delta <= 0.1) return false

    let dur = this.computeDurationForDelta(delta, isRealTime)
    
    // Adjust duration based on velocity for smoother real-time control
    if (isRealTime && Math.abs(this.lastVelocity) > 0.1) {
      // If moving fast in real-time mode, reduce duration for more responsive control
      const velocityFactor = Math.max(0.5, 1 - Math.abs(this.lastVelocity) * 0.01)
      dur = Math.round(dur * velocityFactor)
      const minDur = isRealTime ? this.REALTIME_DURATION_MS : this.MIN_DURATION_MS
      const maxDur = this.MAX_DURATION_MS
      dur = Math.max(minDur, Math.min(dur, maxDur))
    }
    
    if (!dur) return false

    const xp = v / 100
    this.lastCommandSliderVal = v
    this.lastSendTime = performance.now()

    // v3 renames every field: position->xp, duration->t, stopOnTarget->stop_on_target,
    // immediateResponse->immediate_rsp. v2 position is 0-100; v3 xp is 0-1.
    const body = this.apiVersion === "v3"
      ? { stop_on_target: false, immediate_rsp: true, t: dur, xp }
      : { stopOnTarget: false, immediateResponse: true, duration: dur, position: v }

    try {
      const response = await fetch(`${this.baseUrl}/hdsp/xpt`, {
        method: "PUT",
        headers: this.getHeaders(),
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error("[Handy] XPT command failed:", response.status, errorText)
        return false
      }

      return true
    } catch (e) {
      console.error("[Handy] XPT send failed:", e)
      return false
    }
  }

  private scheduleLiveSend(target: number): void {
    // Use optimized real-time parameters for responsive drawing
    const delta = Math.abs(target - this.lastCommandSliderVal)

    if (delta < this.REALTIME_DELTA_THRESHOLD) return

    const now = performance.now()
    const dt = now - this.lastSendTime
    
    // Update position history for velocity calculation
    this.updatePositionHistory(target, now)
    
    this.pendingSliderVal = target

    if (this.sendTimerId) {
      clearTimeout(this.sendTimerId)
      this.sendTimerId = null
    }

    if (dt >= this.REALTIME_MIN_INTERVAL_MS) {
      const v = this.pendingSliderVal
      this.pendingSliderVal = null
      this.sendXPTForSliderVal(v, Math.abs(v - this.lastCommandSliderVal), true)  // Pass isRealTime flag
    } else {
      this.sendTimerId = setTimeout(() => {
        this.sendTimerId = null
        if (this.pendingSliderVal == null) return
        const v = this.pendingSliderVal
        this.pendingSliderVal = null
        this.sendXPTForSliderVal(v, Math.abs(v - this.lastCommandSliderVal), true)  // Pass isRealTime flag
      }, this.REALTIME_MIN_INTERVAL_MS - dt)
    }
  }
  
  // Add separate scheduling for playback vs real-time control
  private schedulePlaybackSend(target: number, durationMs?: number): void {
    // No delta threshold for scripted playback — the engine pre-schedules each action
    // with setTimeout, so they arrive properly spaced. Filtering here drops valid moves.
    const delta = Math.abs(target - this.lastCommandSliderVal)
    const PLAYBACK_MIN_INTERVAL = 40    // Guard against API rate limits only

    if (delta < 0.5) return // Only skip truly identical positions

    const now = performance.now()
    const dt = now - this.lastSendTime
    this.pendingSliderVal = target
    this.pendingDuration = durationMs

    if (this.sendTimerId) {
      clearTimeout(this.sendTimerId)
      this.sendTimerId = null
    }

    if (dt >= PLAYBACK_MIN_INTERVAL) {
      const v = this.pendingSliderVal
      const dur = this.pendingDuration
      this.pendingSliderVal = null
      this.pendingDuration = undefined
      this.sendXPTForPlayback(v, Math.abs(v - this.lastCommandSliderVal), dur)
    } else {
      this.sendTimerId = setTimeout(() => {
        this.sendTimerId = null
        if (this.pendingSliderVal == null) return
        const v = this.pendingSliderVal
        const dur = this.pendingDuration
        this.pendingSliderVal = null
        this.pendingDuration = undefined
        this.sendXPTForPlayback(v, Math.abs(v - this.lastCommandSliderVal), dur)
      }, PLAYBACK_MIN_INTERVAL - dt)
    }
  }

  private async sendXPTForPlayback(v: number, delta: number, durationMs?: number): Promise<boolean> {
    if (!this.isConnected || !this.connectionKey) {
      return false
    }

    if (delta <= 0.1) return false

    // Prefer the engine-provided duration (matches actual script timing for smooth motion).
    // Add 30ms buffer so the device is still in motion when the next command arrives —
    // JS setTimeout has ±4-10ms jitter, and the buffer prevents micro-gaps between actions.
    // Fall back to computed duration only if not provided.
    const dur = durationMs && durationMs > 0
      ? Math.max(50, Math.min(Math.round(durationMs) + 30, 30000))
      : this.computePlaybackDuration(delta)
    if (!dur) return false

    const xp = v / 100
    this.lastCommandSliderVal = v
    this.lastSendTime = performance.now()

    // stopOnTarget/stop_on_target: false — device stays in continuous motion between commands.
    // true would make the device decelerate to a full stop at each action point and wait,
    // which creates the sluggish stop-start stutter. false matches the real-time path behavior.
    const body = this.apiVersion === "v3"
      ? { stop_on_target: false, immediate_rsp: true, t: dur, xp }
      : { stopOnTarget: false, immediateResponse: true, duration: dur, position: v }

    try {
      const response = await fetch(`${this.baseUrl}/hdsp/xpt`, {
        method: "PUT",
        headers: this.getHeaders(),
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error("[Handy] XPT playback command failed:", response.status, errorText)
        return false
      }

      return true
    } catch (e) {
      console.error("[Handy] XPT playback send failed:", e)
      return false
    }
  }

  async holdPosition(position: number): Promise<boolean> {
    if (!this.isConnected || !this.connectionKey) return false

    if (this.sendTimerId) {
      clearTimeout(this.sendTimerId)
      this.sendTimerId = null
    }
    this.pendingSliderVal = null
    this.pendingDuration = undefined

    const scaledPosition =
      this.deviceSettings.minPosition +
      (position / 100) * (this.deviceSettings.maxPosition - this.deviceSettings.minPosition)
    const clampedPosition = Math.max(0, Math.min(100, Math.round(scaledPosition)))

    if (this.isHAMPMode) {
      await this.stopHAMP()
    }
    if (this.lastSetMode !== this.MODE_HDSP) {
      const initialized = await this.initializeHDSP()
      if (!initialized) return false
    }

    const xp = clampedPosition / 100
    this.lastCommandSliderVal = clampedPosition
    this.lastSendTime = performance.now()

    const body = this.apiVersion === "v3"
      ? { stop_on_target: true, immediate_rsp: true, t: 50, xp }
      : { stopOnTarget: true, immediateResponse: true, duration: 50, position: clampedPosition }

    try {
      const response = await fetch(`${this.baseUrl}/hdsp/xpt`, {
        method: "PUT",
        headers: this.getHeaders(),
        body: JSON.stringify(body),
      })
      if (!response.ok) {
        const errorText = await response.text()
        console.error("[Handy] XPT hold command failed:", response.status, errorText)
        return false
      }
      return true
    } catch (e) {
      console.error("[Handy] XPT hold send failed:", e)
      return false
    }
  }

  private computePlaybackDuration(delta: number): number {
    // Original duration calculation for script playback
    if (delta <= 0.1) return 0
    const d = 300 * (delta / 100)  // Original formula
    return Math.max(50, Math.min(Math.round(d), 30000))
  }

  private updatePositionHistory(position: number, timestamp: number): void {
    this.positionHistory.push({ position, timestamp })
    
    // Keep only recent history
    if (this.positionHistory.length > this.HISTORY_SIZE) {
      this.positionHistory.shift()
    }
    
    // Calculate velocity for smoother movement
    if (this.positionHistory.length >= 2) {
      const recent = this.positionHistory[this.positionHistory.length - 1]
      const previous = this.positionHistory[this.positionHistory.length - 2]
      const timeDiff = recent.timestamp - previous.timestamp
      
      if (timeDiff > 0) {
        this.lastVelocity = (recent.position - previous.position) / timeDiff
      }
    }
  }

  // HAMP Mode Methods for velocity-based real-time control
  
  // ── Public manual control API ────────────────────────────────────────────────

  async manualStart(speed = 50): Promise<boolean> {
    const started = await this.startHAMP()
    if (!started) return false
    return this.setHAMPVelocity(Math.max(0, Math.min(100, speed)))
  }

  async manualStop(): Promise<boolean> {
    return this.stopHAMP()
  }

  async manualSetSpeed(speed: number): Promise<boolean> {
    if (!this.isHAMPMode) {
      const started = await this.startHAMP()
      if (!started) return false
    }
    return this.setHAMPVelocity(Math.max(0, Math.min(100, speed)))
  }

  async setSlideRange(min: number, max: number): Promise<boolean> {
    if (!this.connectionKey) return false
    if (this.apiVersion === "v3") {
      // /settings/slider is read-only in v3 ("use the Handy onboarding app
      // to change device slider settings") — there is no PUT. Nothing this
      // proxy can do; the physical stroke zone can only be narrowed/widened
      // from Handy's own app on a v3-connected device.
      console.warn("[Handy] setSlideRange is not supported via API v3 — use the Handy app to set the physical stroke zone.")
      return false
    }
    try {
      const res = await fetch(`${this.baseUrl}/slide`, {
        method: "PUT",
        headers: this.getHeaders(),
        body: JSON.stringify({ min: Math.round(min), max: Math.round(max) }),
      })
      return res.ok
    } catch {
      return false
    }
  }

  getIsHAMPActive(): boolean {
    return this.isHAMPMode
  }

  // ─────────────────────────────────────────────────────────────────────────────

  private async startHAMP(): Promise<boolean> {
    if (this.isHAMPMode) return true
    
    try {
      console.log("[Handy] Starting HAMP mode")
      
      // The HAMP start endpoint switches the device into HAMP mode for us.
      const response = await fetch(`${this.baseUrl}/hamp/start`, {
        method: "PUT",
        headers: this.getHeaders(),
      })
      
      if (!response.ok) {
        console.error("[Handy] Failed to start HAMP mode:", response.status)
        return false
      }
      
      this.isHAMPMode = true
      // lastSetMode here is only ever consulted by the keep-awake ping, which
      // is itself skipped whenever isHAMPMode is true — this never drives a
      // real /mode PUT while HAMP is active. Kept version-aware anyway for
      // consistency with the other lastSetMode assignments in this file.
      this.lastSetMode = this.MODE_HDSP
      this.hampVelocity = 0
      
      console.log("[Handy] HAMP mode started successfully")
      return true
    } catch (error) {
      console.error("[Handy] HAMP start failed:", error)
      return false
    }
  }
  
  private async stopHAMP(): Promise<boolean> {
    if (!this.isHAMPMode) return true
    
    try {
      console.log("[Handy] Stopping HAMP mode")
      
      // Stop HAMP velocity
      await this.setHAMPVelocity(0)
      
      // Stop HAMP mode
      const response = await fetch(`${this.baseUrl}/hamp/stop`, {
        method: "PUT",
        headers: this.getHeaders(),
      })
      
      if (!response.ok) {
        console.error("[Handy] Failed to stop HAMP mode:", response.status)
        return false
      }
      
      this.isHAMPMode = false
      this.lastSetMode = null
      this.hampVelocity = 0
      
      // Clear update interval
      if (this.hampUpdateInterval) {
        clearInterval(this.hampUpdateInterval)
        this.hampUpdateInterval = null
      }
      
      console.log("[Handy] HAMP mode stopped successfully")
      return true
    } catch (error) {
      console.error("[Handy] HAMP stop failed:", error)
      return false
    }
  }
  
  private async setHAMPVelocity(velocity: number): Promise<boolean> {
    if (!this.isHAMPMode || !this.connectionKey) {
      return false
    }
    
    try {
      // Clamp velocity to -100 to 100 range
      const clampedVelocity = Math.max(-100, Math.min(100, velocity))

      // v3's HAMP velocity is unsigned [0-1] magnitude only — there is no
      // documented request field for direction anywhere in /hamp/start,
      // /hamp/velocity, or /hamp/stroke (stroke sets the region bounds, not
      // direction). The negative-velocity "reverse" semantic this codebase
      // relies on for manualStart/manualSetSpeed has no v3 equivalent found
      // in the spec — magnitude carries over, direction is silently dropped.
      // UNVERIFIED against a live v3 device: whether the device auto-bounces
      // or needs some other trigger for direction.
      const body = this.apiVersion === "v3"
        ? { velocity: Math.min(1, Math.abs(clampedVelocity) / 100) }
        : { velocity: clampedVelocity }

      const response = await fetch(`${this.baseUrl}/hamp/velocity`, {
        method: "PUT",
        headers: this.getHeaders(),
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        console.error("[Handy] Failed to set HAMP velocity:", response.status)
        return false
      }

      this.hampVelocity = clampedVelocity
      return true
    } catch (error) {
      console.error("[Handy] HAMP velocity set failed:", error)
      return false
    }
  }
  
  private calculateVelocityFromHistory(): number {
    if (this.positionHistory.length < 2) return 0
    
    // Calculate velocity from recent position changes
    const recent = this.positionHistory[this.positionHistory.length - 1]
    const previous = this.positionHistory[this.positionHistory.length - 2]
    
    const timeDiff = recent.timestamp - previous.timestamp
    if (timeDiff === 0) return 0
    
    const positionDiff = recent.position - previous.position
    
    // Convert position change per ms to velocity percentage
    // Positive = moving up, negative = moving down
    // Scale to -100 to 100 range
    const velocity = (positionDiff / timeDiff) * 1000 // per second
    
    // Store velocity in history for prediction
    this.velocityHistory.push(velocity)
    if (this.velocityHistory.length > this.VELOCITY_HISTORY_SIZE) {
      this.velocityHistory.shift()
    }
    
    // Calculate predicted velocity using acceleration
    const predictedVel = this.predictNextVelocity(velocity)
    
    // Apply smoothing factor with prediction
    const smoothedVelocity = predictedVel * 0.6 + velocity * 0.3 + this.hampVelocity * 0.1
    
    return Math.max(-100, Math.min(100, smoothedVelocity))
  }
  
  // Phase 3: Velocity Prediction
  private predictNextVelocity(currentVelocity: number): number {
    if (this.velocityHistory.length < 2) {
      return currentVelocity
    }
    
    // Calculate acceleration (change in velocity)
    const velocities = this.velocityHistory
    const accelerations: number[] = []
    
    for (let i = 1; i < velocities.length; i++) {
      accelerations.push(velocities[i] - velocities[i - 1])
    }
    
    // Average acceleration
    const avgAcceleration = accelerations.reduce((sum, acc) => sum + acc, 0) / accelerations.length
    
    // Predict next velocity based on current velocity + acceleration
    let predicted = currentVelocity + avgAcceleration
    
    // Detect direction changes (acceleration sign flip)
    if (accelerations.length >= 2) {
      const recentAcc = accelerations[accelerations.length - 1]
      const prevAcc = accelerations[accelerations.length - 2]
      
      // If acceleration is changing direction, reduce prediction confidence
      if (Math.sign(recentAcc) !== Math.sign(prevAcc)) {
        predicted = currentVelocity * 0.8 + predicted * 0.2
      }
    }
    
    // Clamp prediction to reasonable range
    return Math.max(-100, Math.min(100, predicted))
  }
  
  // Phase 3: Detect if user is stopping (deceleration)
  private isDecelerating(): boolean {
    if (this.velocityHistory.length < 2) return false
    
    const recent = this.velocityHistory[this.velocityHistory.length - 1]
    const previous = this.velocityHistory[this.velocityHistory.length - 2]
    
    // Check if velocity magnitude is decreasing
    return Math.abs(recent) < Math.abs(previous) * 0.8
  }
  
  private async setPositionHAMP(position: number): Promise<boolean> {
    if (!this.isHAMPMode) {
      // Start HAMP mode if not already active
      const started = await this.startHAMP()
      if (!started) {
        console.error("[Handy] Failed to start HAMP mode for real-time control")
        return false
      }
    }
    
    // Update position history
    this.updatePositionHistory(position, performance.now())
    
    // Calculate velocity from position changes
    const velocity = this.calculateVelocityFromHistory()
    
    // Only send velocity update if it changed significantly
    const velocityDelta = Math.abs(velocity - this.hampVelocity)
    if (velocityDelta > 5) { // 5% threshold
      await this.setHAMPVelocity(velocity)
    }
    
    return true
  }

  async setPosition(position: number, _duration?: number, isRealTime: boolean = false): Promise<boolean> {
    if (!this.connectionKey) {
      console.error("[Handy] No connection key when trying to set position")
      return false
    }

    const scaledPosition =
      this.deviceSettings.minPosition +
      (position / 100) * (this.deviceSettings.maxPosition - this.deviceSettings.minPosition)

    const clampedPosition = Math.max(0, Math.min(100, Math.round(scaledPosition)))

    // Use scheduleLiveSend (HDSP xpt) for real-time manual control — sends exact position
    // Use schedulePlaybackSend (HDSP xpt with playback params) for script playback
    if (isRealTime) {
      if (this.isHAMPMode) {
        await this.stopHAMP()
      }
      if (this.lastSetMode !== this.MODE_HDSP) {
        const initialized = await this.initializeHDSP()
        if (!initialized) {
          console.error("[Handy] Failed to initialize HDSP for real-time control")
          return false
        }
      }
      this.scheduleLiveSend(clampedPosition)
      return true
    } else {
      // Ensure we're in HDSP mode for script playback
      if (this.isHAMPMode) {
        await this.stopHAMP()
      }

      if (this.lastSetMode !== this.MODE_HDSP) {
        const initialized = await this.initializeHDSP()
        if (!initialized) {
          console.error("[Handy] Failed to initialize HDSP")
          return false
        }
      }

      // Use HDSP scheduling for playback — pass the engine's duration for smooth interpolation
      this.schedulePlaybackSend(clampedPosition, _duration)
      return true
    }
  }

  // HVP (Handy Vibration Protocol) is new in v3 — there is no v2 equivalent
  // at all. UNVERIFIED against a live device: the spec's own text says
  // amplitude/frequency/position apply "For LRA, ERM and Sliders WITH
  // VIBRATION ADAPTER" — a standard Handy slider likely needs that adapter
  // accessory to actually vibrate; without one this may PUT successfully but
  // produce no physical effect. frequency/position are left at fixed
  // reasonable defaults since setVibration's signature has no room for them.
  async setVibration(intensity: number): Promise<boolean> {
    if (this.apiVersion !== "v3") {
      console.log("[Handy] Vibration not supported on API v2")
      return false
    }
    if (!this.connectionKey) return false
    try {
      const amplitude = Math.max(0, Math.min(1, intensity / 100))
      const stateRes = await fetch(`${this.baseUrl}/hvp/state`, {
        method: "PUT",
        headers: this.getHeaders(),
        body: JSON.stringify({ amplitude, frequency: 100, position: 0.5 }),
      })
      if (!stateRes.ok) {
        console.error("[Handy] Failed to set HVP state:", stateRes.status)
        return false
      }
      const endpoint = amplitude > 0 ? "start" : "stop"
      const res = await fetch(`${this.baseUrl}/hvp/${endpoint}`, {
        method: "PUT",
        headers: this.getHeaders(),
      })
      return res.ok
    } catch (error) {
      console.error("[Handy] Set vibration failed:", error)
      return false
    }
  }

  async setRotation(speed: number): Promise<boolean> {
    console.log("[Handy] Rotation not supported")
    return false
  }

  async setOscillation(speed: number): Promise<boolean> {
    console.log("[Handy] Oscillation not supported")
    return false
  }

  async setConstrict(level: number): Promise<boolean> {
    console.log("[Handy] Constrict not supported")
    return false
  }

  async setSurge(position: number, duration?: number): Promise<boolean> {
    console.log("[Handy] Surge not supported")
    return false
  }

  async setSway(position: number, duration?: number): Promise<boolean> {
    console.log("[Handy] Sway not supported")
    return false
  }

  async setRoll(position: number, duration?: number): Promise<boolean> {
    console.log("[Handy] Roll not supported")
    return false
  }

  async setPitch(position: number, duration?: number): Promise<boolean> {
    console.log("[Handy] Pitch not supported")
    return false
  }

  async stop(): Promise<boolean> {
    if (!this.connectionKey) return false

    // Cancel any queued position send immediately
    if (this.sendTimerId) {
      clearTimeout(this.sendTimerId)
      this.sendTimerId = null
    }
    this.pendingSliderVal = null
    this.pendingDuration = undefined

    try {
      if (this.isHAMPMode) {
        await this.stopHAMP()
      }

      // Fire both stop commands in parallel — one will be the right one
      // depending on whether device is in HAMP or HSSP.
      const stops: Promise<unknown>[] = [
        fetch(`${this.baseUrl}/hamp/stop`, { method: "PUT", headers: this.getHeaders() })
          .catch(() => null),
      ]
      if (this.hsspActive) {
        stops.push(
          fetch(`${this.baseUrl}/hssp/stop`, { method: "PUT", headers: this.getHeaders() })
            .catch(() => null),
        )
      }
      if (this.apiVersion === "v3") {
        stops.push(
          fetch(`${this.baseUrl}/hvp/stop`, { method: "PUT", headers: this.getHeaders() })
            .catch(() => null),
        )
      }
      await Promise.all(stops)

      this.lastSetMode = null
      this.hsspActive = false
      return true
    } catch (error) {
      console.error("[Handy] Stop failed:", error)
      return false
    }
  }

  async autoCalibrate(pingCount = 5): Promise<{ latencyMs: number; serverTime: number }> {
    if (!this.connectionKey) {
      throw new Error("No connection key set")
    }

    const latencies: number[] = []

    for (let i = 0; i < pingCount; i++) {
      const start = Date.now()
      try {
        const response = await fetch(`${this.baseUrl}/servertime`, {
          headers: this.getHeaders(),
        })
        const end = Date.now()
        await response.json()

        const rtt = end - start
        latencies.push(rtt / 2)

        await new Promise((r) => setTimeout(r, 100))
      } catch (error) {
        console.error(`[Handy] Calibration ping ${i + 1} failed:`, error)
      }
    }

    if (latencies.length === 0) {
      throw new Error("All calibration pings failed")
    }

    const sorted = latencies.sort((a, b) => a - b)
    const trimmed = sorted.length > 2 ? sorted.slice(1, -1) : sorted
    const avgLatency = Math.round(trimmed.reduce((a, b) => a + b, 0) / trimmed.length)

    const serverResponse = await fetch(`${this.baseUrl}/servertime`, {
      headers: this.getHeaders(),
    })
    const serverData = await serverResponse.json()
    // /servertime is unauthenticated and NOT wrapped in {result} on v3
    // (unlike every other endpoint) — read the field directly, no unwrap().
    const serverTime = this.apiVersion === "v3" ? serverData.server_time : serverData.serverTime

    return {
      latencyMs: avgLatency,
      serverTime,
    }
  }

  async testMovement(): Promise<boolean> {
    if (!this.connectionKey) return false

    try {
      await this.setPosition(20)
      await new Promise((r) => setTimeout(r, 500))
      await this.setPosition(80)
      await new Promise((r) => setTimeout(r, 500))
      await this.setPosition(50)
      return true
    } catch (error) {
      console.error("[Handy] Test movement failed:", error)
      return false
    }
  }

  startConnectionMonitor(onStatusChange: (connected: boolean) => void, intervalMs = 20000) {
    this.stopConnectionMonitor()
    this.connectionMonitorActive = true
    if (this.isConnected) {
      this.startKeepAwake()
    }

    this.pingInterval = setInterval(async () => {
      const wasConnected = this.isConnected
      await this.checkConnection()

      if (wasConnected !== this.isConnected) {
        onStatusChange(this.isConnected)
      }
    }, intervalMs)
  }

  stopConnectionMonitor() {
    this.connectionMonitorActive = false
    if (this.pingInterval) {
      clearInterval(this.pingInterval)
      this.pingInterval = null
    }
    this.stopKeepAwake()
  }

  getIsConnected(): boolean {
    return this.isConnected
  }

  disconnect() {
    this.connectionKey = null
    this.isConnected = false
    this.lastSetMode = null
    this.stopConnectionMonitor()
    this.stopKeepAwake()
    
    // Stop HAMP mode if active
    if (this.isHAMPMode) {
      this.isHAMPMode = false
      if (this.hampUpdateInterval) {
        clearInterval(this.hampUpdateInterval)
        this.hampUpdateInterval = null
      }
    }
    
    // Clear any pending send timer to prevent memory leaks
    if (this.sendTimerId) {
      clearTimeout(this.sendTimerId)
      this.sendTimerId = null
    }
    
    // Reset pending values and history
    this.pendingSliderVal = null
    this.positionHistory = []
    this.lastVelocity = 0
    this.hampVelocity = 0
    this.velocityHistory = []
    this.predictedVelocity = 0
  }

  setDeviceSettings(settings: { minPosition?: number; maxPosition?: number }) {
    if (settings.minPosition !== undefined) {
      this.deviceSettings.minPosition = settings.minPosition
    }
    if (settings.maxPosition !== undefined) {
      this.deviceSettings.maxPosition = settings.maxPosition
    }
  }

  // ─── HSSP (Handy Server Sync Protocol) ───────────────────────────────────
  // For pre-authored scripts: upload CSV once, sync clock, let device handle
  // all timing internally. Eliminates per-command HTTP latency entirely.

  private hsspActive = false
  private serverTimeOffsetMs = 0
  private serverTimeSyncedAt = 0  // Date.now() of last successful syncServerClock
  private uploadCache: { hash: string; url: string; sha256: string; filename: string } | null = null
  private lastSetupUrl: string | null = null  // URL of script currently loaded on device

  /** Lightweight hash of actions — detects same vs different script without full serialization */
  private hashActions(actions: Array<{ at: number; pos: number }>): string {
    return hashActionsContent(actions)
  }

  /** Upload actions to Supabase via API route; returns URL + sha256 the device can fetch */
  async prepareHSSP(actions: Array<{ at: number; pos: number }>): Promise<{
    url: string
    sha256: string
    filename: string
  } | null> {
    const hash = this.hashActions(actions)
    if (this.uploadCache?.hash === hash) {
      console.log("[Handy HSSP] Using cached upload")
      return { url: this.uploadCache.url, sha256: this.uploadCache.sha256, filename: this.uploadCache.filename }
    }
    try {
      const resp = await fetch("/api/handy/hssp-upload", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ actions }),
      })
      if (!resp.ok) {
        console.warn("[Handy HSSP] Upload failed:", await resp.text())
        return null
      }
      const result = await resp.json()
      this.uploadCache = { hash, ...result }
      return result
    } catch (err) {
      console.warn("[Handy HSSP] prepareHSSP error:", err)
      return null
    }
  }

  /** Tell the device to download and prepare the script from a URL.
   *  Sets HSSP mode first, triggers download, then polls until the script is loaded (v2 state 3/4).
   *  Skips the download if the device already has this exact script loaded. */
  async setupHSSP(url: string, sha256: string): Promise<boolean> {
    if (!this.connectionKey) return false
    try {
      const stayedInHSSP = this.lastSetMode === this.MODE_HSSP
      // 1. Switch to HSSP mode — skip if already set
      if (this.lastSetMode !== this.MODE_HSSP) {
        const modeResp = await fetch(`${this.baseUrl}/mode`, {
          method: "PUT",
          headers: this.getHeaders(),
          body: JSON.stringify({ mode: this.MODE_HSSP }),
        })
        if (!modeResp.ok) {
          console.warn("[Handy HSSP] Failed to set HSSP mode:", modeResp.status)
          return false
        }
        this.lastSetMode = this.MODE_HSSP
        // Give the device a moment to finish switching modes before accepting setup
        await new Promise(r => setTimeout(r, 300))
      }

      // 2. If the device already has this exact script loaded, skip re-download
      // A stopped state after HDSP does not prove the old HSSP script is initialized.
      // Reissue setup after a scrub/manual mode change, even when the URL is unchanged.
      if (stayedInHSSP && this.lastSetupUrl === url) {
        if (await this.isHSSPReadyNow()) {
          console.log("[Handy HSSP] Script already on device — skipping download")
          return true
        }
      }

      // 3. Tell the device to download the script — retry up to 3× on gateway errors
      // v3's /hssp/setup drops the sha256 checksum param entirely (no verification
      // field in its schema) — sent only on v2.
      const setupBody = this.apiVersion === "v3" ? { url } : { url, sha256 }
      let setupOk = false
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const resp = await fetch(`${this.baseUrl}/hssp/setup`, {
            method: "PUT",
            headers: this.getHeaders(),
            body: JSON.stringify(setupBody),
          })
          if (resp.ok) {
            const acknowledgment = await resp.json()
            console.log("[Handy HSSP] Setup acknowledgment:", JSON.stringify(acknowledgment))
            if (!acknowledgment?.error && (this.apiVersion !== "v2" || acknowledgment?.result === 0 || acknowledgment?.result === 1)) {
              setupOk = true
              break
            }
            console.warn("[Handy HSSP] Device rejected setup:", JSON.stringify(acknowledgment))
            return false
          }
          const errText = await resp.text().catch(() => resp.status.toString())
          console.warn(`[Handy HSSP] setup attempt ${attempt} failed (${resp.status}):`, errText)
          if (attempt < 3) await new Promise(r => setTimeout(r, 1000 * attempt))
        } catch (fetchErr) {
          console.warn(`[Handy HSSP] setup attempt ${attempt} network error:`, fetchErr)
          if (attempt < 3) await new Promise(r => setTimeout(r, 1000 * attempt))
        }
      }
      if (!setupOk) {
        console.warn("[Handy HSSP] setup failed after 3 attempts")
        return false
      }

      // 4. Poll /hssp/state until ready. Handy's cloud/device side can take
      // longer than 15s on first download, and some responses expose readiness
      // as scriptSet=true rather than the older numeric ready state.
      const ready = await this.waitForHSSPReady(45_000)
      if (!ready) {
        console.warn("[Handy HSSP] Device did not finish downloading script in time")
        return false
      }

      this.lastSetupUrl = url
      console.log("[Handy HSSP] Script ready on device")
      return true
    } catch (err) {
      console.warn("[Handy HSSP] setupHSSP error:", err)
      return false
    }
  }

  // v2 SDK states: 1=NEED_SYNC, 2=NEED_SETUP, 3=STOPPED, 4=PLAYING.
  // v3 renames the field to play_state with a DIFFERENT enum entirely:
  // 0=NOT_INITIALIZED, 1=PLAYING, 2=STOPPED, 3=PAUSED, 4=STARVING — and has
  // no distinct "downloaded but not yet playing" state exposed, so "loaded"
  // is treated as "anything but NOT_INITIALIZED". UNVERIFIED against a live
  // v3 device — the v3 spec doesn't show a downloading-in-progress example.
  private isHsspLoaded(state: number, data?: any): boolean {
    if (data?.scriptSet === true || data?.script_set === true) return true
    return this.apiVersion === "v3" ? state !== 0 : state === 3 || state === 4
  }

  /** Single-shot HSSP ready check (no polling) */
  private async isHSSPReadyNow(): Promise<boolean> {
    try {
      const resp = await fetch(`${this.baseUrl}/hssp/state`, { headers: this.getHeaders() })
      if (resp.ok) {
        const data = this.unwrap<any>(await resp.json())
        const state = (this.apiVersion === "v3" ? data?.play_state : data?.state) ?? 0
        return this.isHsspLoaded(state, data)
      }
    } catch { /* network hiccup */ }
    return false
  }

  /** Poll /hssp/state until ready or timeout */
  private async waitForHSSPReady(timeoutMs: number): Promise<boolean> {
    const deadline = Date.now() + timeoutMs
    while (Date.now() < deadline) {
      try {
        const resp = await fetch(`${this.baseUrl}/hssp/state`, { headers: this.getHeaders() })
        if (resp.ok) {
          const data = this.unwrap<any>(await resp.json())
          const state = (this.apiVersion === "v3" ? data?.play_state : data?.state) ?? 0
          console.log("[Handy HSSP] state:", JSON.stringify(data))
          if (this.isHsspLoaded(state, data)) return true
          // still downloading / not initialized yet — keep polling
        }
      } catch { /* network hiccup, keep polling */ }
      await new Promise(r => setTimeout(r, 500))
    }
    return false
  }

  /** Measure offset between our clock and Handy's server clock (run before playHSSP) */
  async syncServerClock(pings = 3): Promise<boolean> {
    if (!this.connectionKey) return false
    // Re-use cached offset if synced within the last 60 seconds
    if (this.serverTimeSyncedAt > 0 && Date.now() - this.serverTimeSyncedAt < 60_000) {
      console.log("[Handy HSSP] Using cached clock offset:", Math.round(this.serverTimeOffsetMs), "ms")
      return true
    }
    const offsets: number[] = []
    for (let i = 0; i < pings; i++) {
      try {
        const t0 = Date.now()
        const resp = await fetch(`${this.baseUrl}/servertime`, { headers: this.getHeaders() })
        const t1 = Date.now()
        if (!resp.ok) continue
        // /servertime is unwrapped even on v3 — see autoCalibrate() for the same case.
        const raw = await resp.json()
        const serverTime = this.apiVersion === "v3" ? raw.server_time : raw.serverTime
        if (!Number.isFinite(serverTime)) continue
        const rtt = t1 - t0
        // Our estimate of server time at the midpoint of the request
        offsets.push(Date.now() - serverTime - rtt / 2)
        if (i < pings - 1) await new Promise(r => setTimeout(r, 80))
      } catch {
        // Skip failed pings
      }
    }
    if (offsets.length === 0) return false
    this.serverTimeOffsetMs = offsets.reduce((a, b) => a + b) / offsets.length
    this.serverTimeSyncedAt = Date.now()
    console.log("[Handy HSSP] Server clock offset:", Math.round(this.serverTimeOffsetMs), "ms")
    return true
  }

  /** Start HSSP playback from a given script offset (ms). offsetMs=0 = play from start.
   *  Assumes setupHSSP() has already been called and device is in HSSP mode.
   *
   *  API contract (confirmed from official site network logs):
   *  - estimatedServerTime = current server time (Unix ms) — device uses for clock correction
   *  - startTime = script position in ms to start from (NOT a server timestamp)
   */
  async playHSSP(offsetMs = 0): Promise<boolean> {
    if (!this.connectionKey) return false
    try {
      const serverTime = Date.now() - this.serverTimeOffsetMs

      // v3 renames estimatedServerTime->server_time, startTime->start_time —
      // same values, same meaning, just snake_case.
      const playBody = this.apiVersion === "v3"
        ? { server_time: Math.round(serverTime), start_time: Math.round(offsetMs) }
        : { estimatedServerTime: Math.round(serverTime), startTime: Math.round(offsetMs) }

      const resp = await fetch(`${this.baseUrl}/hssp/play`, {
        method: "PUT",
        headers: this.getHeaders(),
        body: JSON.stringify(playBody),
      })

      if (!resp.ok) {
        console.warn("[Handy HSSP] play failed:", resp.status, await resp.text())
        return false
      }

      const acknowledgment = await resp.json()
      console.log("[Handy HSSP] Play acknowledgment:", JSON.stringify(acknowledgment))
      if (acknowledgment?.error || (this.apiVersion === "v2" && acknowledgment?.result !== 0)) {
        console.warn("[Handy HSSP] Device rejected playback:", JSON.stringify(acknowledgment))
        this.hsspActive = false
        return false
      }

      this.hsspActive = true
      console.log("[Handy HSSP] Playback started from", offsetMs, "ms")
      return true
    } catch (err) {
      console.warn("[Handy HSSP] playHSSP error:", err)
      return false
    }
  }

  async getHSSPPlaybackState(): Promise<"playing" | "paused" | "ready" | "unknown"> {
    if (!this.connectionKey) return "unknown"
    try {
      const response = await fetch(`${this.baseUrl}/hssp/state`, { headers: this.getHeaders() })
      if (!response.ok) return "unknown"
      const data = this.unwrap<any>(await response.json())
      const state = this.apiVersion === "v3" ? data?.play_state : data?.state
      if (this.apiVersion === "v3") {
        if (state === 1) return "playing"
        if (state === 3) return "paused"
        if (state === 2) return "ready"
        return "unknown"
      }
      if (state === 4) return "playing"
      if (state === 3) return "ready"
      return "unknown"
    } catch {
      return "unknown"
    }
  }

  /** Switch device back to HDSP mode so position commands work after an HSSP failure */
  async resetToHDSPMode(): Promise<void> {
    if (!this.connectionKey) return
    // Skip the roundtrip if we already know the device is in HDSP mode.
    // This makes subsequent play presses instant instead of adding ~200ms per press.
    if (this.lastSetMode === this.MODE_HDSP) return
    this.lastSetMode = null
    try {
      await fetch(`${this.baseUrl}/mode`, {
        method: "PUT",
        headers: this.getHeaders(),
        body: JSON.stringify({ mode: this.MODE_HDSP }),
      })
      this.lastSetMode = this.MODE_HDSP
      console.log("[Handy] Reset to HDSP mode (", this.MODE_HDSP, ")")
    } catch { /* best-effort */ }
  }

  /** Stop HSSP playback */
  async stopHSSP(): Promise<boolean> {
    if (!this.connectionKey) return false
    try {
      const resp = await fetch(`${this.baseUrl}/hssp/stop`, {
        method: "PUT",
        headers: this.getHeaders(),
      })
      this.hsspActive = false
      return resp.ok
    } catch (err) {
      console.warn("[Handy HSSP] stopHSSP error:", err)
      return false
    }
  }

  /** Delete the temp CSV from Supabase. Clears local caches so the next play re-uploads. */
  async cleanupHSSP(filename: string): Promise<void> {
    // Clear caches so the next setup knows the file is gone
    this.uploadCache = null
    this.lastSetupUrl = null
    try {
      await fetch("/api/handy/hssp-upload", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filename }),
      })
    } catch {
      // Non-critical
    }
  }

  getHsspActive(): boolean {
    return this.hsspActive
  }

  /** A cached engine upload is usable only while this adapter still has that script in HSSP mode. */
  isHSSPPrepared(url: string): boolean {
    return this.lastSetMode === this.MODE_HSSP && this.lastSetupUrl === url
  }
}
